import { useDisclosure } from '@mantine/hooks'
import { openStackemChartsModal } from 'App/Map/CurrentValuesMap/components/SelectedMapCard/components/_utils/openStackemChartsModal'
import { CounterBadge } from 'components/CounterBadge/CounterBadge'
import { isNil, isNumber } from 'lodash'
import { useEffect, useRef } from 'react'
import { HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD } from 'settings/hubConnectorEnvironment'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { mapSelectedCardStore } from 'stores/mapSelectedCardStore/mapSelectedCardStore'
import { mapStore } from 'stores/mapStore/mapStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { usePrimaryValueGroup } from 'utils/usePrimaryValueGroup'
import { useSetMapOverlayZIndex } from 'utils/useSetMapOverlayZIndex'
import type {
  IRRIGATION_ZONE_VALUE_META,
  TIrrigationZonesValueTypesToPropsDictionary,
} from '../IrrigationZoneValuesCache'
import { BadgeContent } from './BadgeContent'

export type TIrrigationZoneBadgeComponentProps = {
  meta: IRRIGATION_ZONE_VALUE_META
  valueTypesToPropsDictionary: TIrrigationZonesValueTypesToPropsDictionary
}

export const IrrigationZoneValueBadgeComponent = ({
  meta,
  valueTypesToPropsDictionary,
}: TIrrigationZoneBadgeComponentProps) => {
  const [isHovered, { close, open }] = useDisclosure(false)
  const primaryValueGroup = usePrimaryValueGroup()

  const { showMapBadgeNames, valueType } = mapControlsStore.useSelector((s) => ({
    showMapBadgeNames: s.showMapBadgeNames,
    valueType: !primaryValueGroup
      ? null
      : s.mapVisualValueGroup.IRRIGATION_ZONE?.[primaryValueGroup]?.valueType,
  }))

  const zoom = mapStore.useSelector((s) => s.zoom)
  const selectedZone = selectedFieldAssetsStore.useSelector((s) => s.irrigationZoneEmitter)
  const ref = useRef<HTMLDivElement>(null)
  const zoneEmitterKey = meta.irrigationZoneEmitterId
  const isSelected = zoneEmitterKey === selectedZone
  const height = 30

  useSetMapOverlayZIndex({ ref, mItem: 'M_IrrigationZone', isSelected, isHighlighted: false, isHovered })

  useEffect(() => {
    close()
  }, [zoom])

  if (isNil(valueType)) return null

  const { baseZIndex, children, getContainerCSS, progressText, progressPercent, valuesForZone } =
    valueTypesToPropsDictionary?.[valueType] ?? {}

  const containerCSS = getContainerCSS?.() ?? {}

  if (progressPercent) containerCSS.backgroundColor = 'transparent'

  const irrigationActivityPointEmitterCount = Object.keys(valuesForZone ?? {}).length

  return (
    <div
      ref={ref}
      onClick={() => {
        setSelectedFieldAsset({ irrigationZoneEmitter: meta.irrigationZoneEmitterId })

        mapSelectedCardStore.actions.showIrrigationActivityZone({
          ...meta,
          irrigationActivity: valuesForZone,
        })

        if (HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD) {
          openStackemChartsModal()
        }
      }}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      {irrigationActivityPointEmitterCount > 1 && (
        <CounterBadge
          count={irrigationActivityPointEmitterCount}
          baseZIndex={baseZIndex + 2}
          offset={{
            top: 4,
            right: -2,
          }}
        />
      )}
      <BadgeContent
        showLabel={isHovered || showMapBadgeNames}
        height={height}
        containerCSS={containerCSS}
        isSelected={isSelected}
        irrigationZoneName={meta.irrigationZoneName}
        progressText={progressText}
        progressPercent={isNumber(progressPercent) ? progressPercent : undefined}
      >
        {children}
      </BadgeContent>
    </div>
  )
}
